import Link from 'next/link';
import LinkButton from '@/components/ui/LinkButton';
import AppIcon from '@/components/ui/AppIcon';
import Image from 'next/image';

interface DiveSiteCard {
  id: number;
  title: string;
  subtitle: string;
  caption: string;
  alt: string;
  image: string;
  href: string;
  diveType: string;
  diveTypeAlt: string;
}

interface FeaturedDiveSitesProps {
  className?: string;
}

// ─── Custom dive-type SVG icons ───────────────────────────────────────────────

function WreckIcon({ size = 14, color = 'currentColor', strokeWidth = 1.6 }: { size?: number; color?: string; strokeWidth?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 20 Q16 26 28 20" />
      <line x1="4" y1="20" x2="6" y2="14" />
      <line x1="28" y1="20" x2="26" y2="14" />
      <line x1="6" y1="14" x2="26" y2="14" />
      <rect x="11" y="9" width="10" height="5" rx="1" />
      <rect x="14" y="5" width="4" height="4" rx="0.5" />
      <circle cx="9" cy="17" r="1.2" />
      <circle cx="23" cy="17" r="1.2" />
      <line x1="16" y1="5" x2="16" y2="3" />
      <line x1="13" y1="4" x2="19" y2="4" />
    </svg>
  );
}

function ReefIcon({ size = 14, color = 'currentColor', strokeWidth = 1.6 }: { size?: number; color?: string; strokeWidth?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <line x1="16" y1="28" x2="16" y2="14" />
      <line x1="16" y1="20" x2="11" y2="14" />
      <line x1="16" y1="17" x2="21" y2="11" />
      <line x1="11" y1="14" x2="8" y2="10" />
      <line x1="11" y1="14" x2="13" y2="9" />
      <line x1="21" y1="11" x2="19" y2="7" />
      <line x1="21" y1="11" x2="24" y2="8" />
      <line x1="16" y1="14" x2="16" y2="9" />
      <circle cx="8" cy="9.5" r="1.2" fill={color} stroke="none" />
      <circle cx="13" cy="8.5" r="1.2" fill={color} stroke="none" />
      <circle cx="16" cy="8.5" r="1.2" fill={color} stroke="none" />
      <circle cx="19" cy="6.5" r="1.2" fill={color} stroke="none" />
      <circle cx="24" cy="7.5" r="1.2" fill={color} stroke="none" />
      <line x1="4" y1="28" x2="28" y2="28" />
    </svg>
  );
}

function CaveIcon({ size = 14, color = 'currentColor', strokeWidth = 1.6 }: { size?: number; color?: string; strokeWidth?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 26 L5 14 Q5 6 16 6 Q27 6 27 14 L27 26" />
      <path d="M9 26 L9 15 Q9 10 16 10 Q23 10 23 15 L23 26" />
      <line x1="16" y1="10" x2="16" y2="14" strokeOpacity="0.5" />
      <line x1="13" y1="11" x2="12" y2="15" strokeOpacity="0.4" />
      <line x1="19" y1="11" x2="20" y2="15" strokeOpacity="0.4" />
      <line x1="5" y1="18" x2="9" y2="18" />
      <line x1="23" y1="18" x2="27" y2="18" />
      <line x1="5" y1="22" x2="9" y2="22" />
      <line x1="23" y1="22" x2="27" y2="22" />
    </svg>
  );
}

function WallIcon({ size = 14, color = 'currentColor', strokeWidth = 1.6 }: { size?: number; color?: string; strokeWidth?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="4" x2="12" y2="28" />
      <line x1="12" y1="8" x2="5" y2="8" />
      <line x1="12" y1="13" x2="5" y2="13" />
      <line x1="12" y1="18" x2="5" y2="18" />
      <line x1="12" y1="23" x2="5" y2="23" />
      <line x1="5" y1="8" x2="7" y2="10" />
      <line x1="5" y1="13" x2="8" y2="15" />
      <line x1="5" y1="18" x2="6" y2="20" />
      <line x1="22" y1="8" x2="22" y2="24" />
      <polyline points="19,21 22,25 25,21" />
      <line x1="12" y1="4" x2="28" y2="4" />
    </svg>
  );
}

function ShoreIcon({ size = 14, color = 'currentColor', strokeWidth = 1.6 }: { size?: number; color?: string; strokeWidth?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 14 Q7 10 11 14 Q15 18 19 14 Q23 10 29 14" />
      <path d="M3 19 Q7 15 11 19 Q15 23 19 19 Q23 15 29 19" />
      <path d="M3 24 Q8 21 13 24 Q18 27 23 24 Q26 22 29 24" strokeOpacity="0.5" />
      <line x1="3" y1="28" x2="29" y2="28" />
      <circle cx="16" cy="28" r="2" />
      <line x1="16" y1="26" x2="16" y2="22" />
    </svg>
  );
}

function ArtificialReefIcon({ size = 14, color = 'currentColor', strokeWidth = 1.6 }: { size?: number; color?: string; strokeWidth?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 24 L6 28 L26 28 L24 24 Z" />
      <path d="M8 24 Q8 14 16 12 Q24 14 24 24" />
      <circle cx="12" cy="22" r="2" />
      <circle cx="20" cy="22" r="2" />
      <circle cx="16" cy="17" r="2" />
      <line x1="16" y1="12" x2="16" y2="8" />
      <line x1="16" y1="10" x2="13" y2="7" />
      <line x1="16" y1="10" x2="19" y2="7" />
      <circle cx="13" cy="6.5" r="1" fill={color} stroke="none" />
      <circle cx="19" cy="6.5" r="1" fill={color} stroke="none" />
      <circle cx="16" cy="7.5" r="1" fill={color} stroke="none" />
    </svg>
  );
}

function TechnicalIcon({ size = 14, color = 'currentColor', strokeWidth = 1.6 }: { size?: number; color?: string; strokeWidth?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="16" cy="18" rx="4" ry="6" />
      <rect x="19" y="13" width="3" height="8" rx="1.5" />
      <rect x="22.5" y="13" width="3" height="8" rx="1.5" />
      <line x1="20.5" y1="13" x2="20.5" y2="11" />
      <line x1="24" y1="13" x2="24" y2="11" />
      <line x1="19.5" y1="11" x2="25.5" y2="11" />
      <path d="M19 16 Q17 14 15 15" strokeDasharray="1.5 1" />
      <circle cx="16" cy="11" r="3" />
      <line x1="13.5" y1="11" x2="18.5" y2="11" strokeOpacity="0.6" />
      <path d="M12 22 Q9 24 8 27" />
      <path d="M20 22 Q23 24 24 27" />
      <circle cx="10" cy="17" r="2.5" />
      <line x1="10" y1="17" x2="11.2" y2="15.8" />
    </svg>
  );
}

type DiveIconComponent = React.FC<{ size?: number; color?: string; strokeWidth?: number }>;

const DIVE_TYPE_SVG_MAP: Record<string, DiveIconComponent> = {
  Wreck: WreckIcon,
  Reef: ReefIcon,
  Cave: CaveIcon,
  Wall: WallIcon,
  'Artificial Reef': ArtificialReefIcon,
  Shore: ShoreIcon,
  Technical: TechnicalIcon,
};

const DIVE_TYPE_ACCENT: Record<string, string> = {
  Wreck: '#FF8A3D',
  Reef: '#6FD3E0',
  Cave: '#a78bfa',
  Wall: '#38bdf8',
  'Artificial Reef': '#4ade80',
  Shore: '#6FD3E0',
  Technical: '#f87171',
};

const diveSiteCards: DiveSiteCard[] = [
  {
    id: 1,
    title: 'MS Zenobia',
    subtitle: 'World-class wreck dive',
    caption: 'One of the world\'s top wreck dives, known for its scale, history, and marine life.',
    alt: 'Zenobia shipwreck Larnaca Cyprus scuba diving wreck dive',
    image: '/assets/images/zenobia-wreck-cyprus-scuba-diving-larnaca-1777197346785.jpg',
    href: '/dive-sites/zenobia',
    diveType: 'Wreck',
    diveTypeAlt: 'Cyprus wreck diving icon — sunken ship dive site',
  },
  {
    id: 2,
    title: 'MUSAN',
    subtitle: 'Underwater sculpture museum',
    caption: 'A surreal underwater forest of sculptures blending art, diving, and marine life.',
    alt: 'MUSAN underwater museum Ayia Napa Cyprus sculptures diving Jason deCaires Taylor',
    image: '/assets/images/musan-underwater-museum-cyprus-diving-ayia-napa-1777197381508.jpg',
    href: '/musan-underwater-museum-cyprus',
    diveType: 'Artificial Reef',
    diveTypeAlt: 'Cyprus artificial reef diving icon — man-made reef structure dive site',
  },
  {
    id: 3,
    title: 'Green Bay',
    subtitle: 'Beginner-friendly shore dive',
    caption: 'Calm, crystal-clear waters perfect for first dives and relaxed exploration.',
    alt: 'Green Bay Protaras Cyprus beginner scuba diving clear water',
    image: '/assets/images/green-bay-cyprus-underwater-protaras-diving-1777197327475.jpg',
    href: '/green-bay-diving-cyprus',
    diveType: 'Shore',
    diveTypeAlt: 'Cyprus shore diving icon — beginner-friendly shore entry dive site',
  },
  {
    id: 4,
    title: 'Jubilee Shoals',
    subtitle: 'Advanced reef dive',
    caption: 'A large natural reef system with pinnacles, walls and caverns off the Limassol coastline.',
    alt: 'Reef wall at Jubilee Shoals, Limassol, Cyprus, with schools of fish and clear blue water',
    image: '/assets/images/jubilee-shoals-limassol-reef-wall-diving-cyprus-1777211934596.png',
    href: '/jubilee-shoals-cyprus',
    diveType: 'Wall',
    diveTypeAlt: 'Cyprus wall diving icon — reef wall and drop-off dive site',
  },
];

const FeaturedDiveSites = ({ className = '' }: FeaturedDiveSitesProps) => {
  return (
    <section
      id="dive-sites-section"
      className={`py-12 sm:py-14 md:py-16 ${className}`}
      style={{
        background: '#071a24',
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <div className="container mx-auto px-4 sm:px-6">

        {/* Section Header */}
        <div className="mb-8 sm:mb-10 md:mb-12">
          <h2
            className="text-2xl sm:text-[1.7rem] md:text-[2.1rem] font-bold mb-3 sm:mb-4 tracking-tight"
            style={{ color: '#e6edf3' }}
          >
            Featured Dive Sites in Cyprus
          </h2>
          {/* SEO intro text with contextual internal links */}
          <p
            className="text-sm sm:text-base md:text-base max-w-3xl leading-relaxed"
            style={{ color: '#a8b3bd' }}
          >
            Cyprus offers some of the best scuba diving in the Mediterranean, including world-class wrecks, reefs, caves, and beginner-friendly shore dives. Highlights include the{' '}
            <Link
              href="/dive-sites/zenobia"
              className="font-semibold underline underline-offset-2 transition-colors duration-150 hover:opacity-80"
              style={{ color: '#6FD3E0' }}
            >
              Zenobia wreck
            </Link>
            {' '}in Larnaca,{' '}
            <Link
              href="/green-bay-diving-cyprus"
              className="font-semibold underline underline-offset-2 transition-colors duration-150 hover:opacity-80"
              style={{ color: '#6FD3E0' }}
            >
              Green Bay
            </Link>
            {' '}in Protaras, and the dramatic sea caves of{' '}
            <Link
              href="/dive-sites/ayia-napa-protaras"
              className="font-semibold underline underline-offset-2 transition-colors duration-150 hover:opacity-80"
              style={{ color: '#6FD3E0' }}
            >
              Cape Greco
            </Link>
            .
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 md:gap-6 mb-8 sm:mb-10">
          {diveSiteCards.map((card) => {
            const accent = DIVE_TYPE_ACCENT[card.diveType] ?? '#6FD3E0';
            const DiveIcon = DIVE_TYPE_SVG_MAP[card.diveType];
            return (
              <Link
                key={card.id}
                href={card.href}
                className="group flex flex-col rounded-2xl overflow-hidden transition-all duration-250"
                style={{
                  background: '#0F2234',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.borderColor = `${accent}44`;
                  el.style.transform = 'translateY(-3px)';
                  el.style.boxShadow = `0 8px 28px rgba(0,0,0,0.45)`;
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.borderColor = 'rgba(255,255,255,0.08)';
                  el.style.transform = 'translateY(0)';
                  el.style.boxShadow = 'none';
                }}
              >
                {/* Image */}
                <div className="relative w-full overflow-hidden" style={{ height: 180 }}>
                  <Image
                    src={card.image}
                    alt={card.alt}
                    fill
                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                  />
                  {/* Gradient overlay */}
                  <div
                    className="absolute inset-0"
                    style={{
                      background: 'linear-gradient(to top, rgba(7,26,36,0.85) 0%, rgba(7,26,36,0.2) 60%, transparent 100%)',
                    }}
                  />
                  {/* Dive type badge on image */}
                  <div
                    className="absolute bottom-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-md"
                    style={{
                      background: 'rgba(7,26,36,0.82)',
                      border: `1px solid ${accent}55`,
                    }}
                  >
                    {DiveIcon && (
                      <DiveIcon size={14} color={accent} strokeWidth={1.8} />
                    )}
                    <span
                      className="text-xs font-semibold"
                      style={{ color: accent }}
                    >
                      {card.diveType}
                    </span>
                  </div>
                </div>

                {/* Card body */}
                <div className="flex flex-col flex-1 p-4">
                  <p
                    className="text-xs font-semibold mb-1 uppercase tracking-wider"
                    style={{ color: accent }}
                  >
                    {card.subtitle}
                  </p>
                  <h3
                    className="text-base font-bold mb-2 leading-tight"
                    style={{ color: '#e6edf3' }}
                  >
                    {card.title}
                  </h3>
                  <p
                    className="text-sm leading-snug"
                    style={{ color: '#a8b3bd' }}
                  >
                    {card.caption}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center">
          <LinkButton
            href="/dive-sites"
            variant="primary"
            size="lg"
            leftIcon={<AppIcon name="MapIcon" size={22} />}
            rightIcon={null}
            style={{}}
            target="_self"
            rel="noopener noreferrer"
          >
            Explore all Cyprus dive sites (40+)
          </LinkButton>
        </div>

      </div>
    </section>
  );
};

export default FeaturedDiveSites;